'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useProject } from '@/contexts/ProjectContext';
import { useAuth } from '@/app/auth-provider';
import type { WebsitePreviewMetadata } from '@/components/github/GitHubLivePreview';
import type {
  GitHubPreviewBrowserContextSnapshot,
  GitHubPreviewContextReference,
} from '@/lib/github/preview/browser-context-shared';
import type {
  CanvasContextReference,
  CanvasContextSnapshot,
  CanvasElementSelection,
  CanvasToolMetadata,
} from '@/lib/canvas/shared';
import { requestAppRailCollapse } from '@/components/core/app-layout/app-rail-state';
import { PreviewAnnotationProvider } from '@/components/preview/PreviewAnnotationContext';
import type { PreviewAnnotationTarget } from '@/lib/preview/annotation';

export type LivingDocsPanelView =
  | 'tools'
  | 'document'
  | 'comments'
  | 'website-preview'
  | 'canvas'
  | 'design-system';

interface LivingDocsPanelContextValue {
  activeReportId: string | null;
  open: boolean;
  view: LivingDocsPanelView;
  activeWebsitePreview: WebsitePreviewMetadata | null;
  websitePreviewSessionId: string | null;
  activeCanvas: CanvasToolMetadata | null;
  openToolChooser: () => void;
  requestClosePanel: () => void;
  panelHasCloseControl: boolean;
  openPanel: (reportId?: string | null) => void;
  openComments: (reportId: string) => void;
  openWebsitePreview: (preview?: WebsitePreviewMetadata | null) => void;
  updateWebsitePreview: (preview: WebsitePreviewMetadata) => void;
  toggleWebsitePreview: (preview: WebsitePreviewMetadata) => void;
  openCanvas: (canvas?: CanvasToolMetadata | null) => void;
  openDesignSystem: () => void;
  updateCanvas: (canvas: CanvasToolMetadata) => void;
  toggleCanvas: (canvas: CanvasToolMetadata) => void;
  clearCanvas: (expectedCanvasId?: string) => void;
  setCanvasSelection: (selection: CanvasElementSelection | null, version?: number | null) => void;
  registerCanvasSelectionClear: (handler: (() => void) | null) => void;
  closePanel: () => void;
  selectReport: (reportId: string | null) => void;
  setView: (view: LivingDocsPanelView) => void;
}

interface LivingDocsPanelActionsContextValue {
  registerPanelCloseHandler: (handler: () => void) => () => void;
  openPanel: (reportId?: string | null) => void;
  openComments: (reportId: string) => void;
  openWebsitePreview: (preview?: WebsitePreviewMetadata | null) => void;
  updateWebsitePreview: (preview: WebsitePreviewMetadata) => void;
  toggleWebsitePreview: (preview: WebsitePreviewMetadata) => void;
  openCanvas: (canvas?: CanvasToolMetadata | null) => void;
  openDesignSystem: () => void;
  updateCanvas: (canvas: CanvasToolMetadata) => void;
  toggleCanvas: (canvas: CanvasToolMetadata) => void;
  clearCanvas: (expectedCanvasId?: string) => void;
  closePanel: () => void;
  selectReport: (reportId: string | null) => void;
  getActiveReportId: () => string | null;
  getSelectedReportId: () => string | null;
  getWebsitePreviewContextSnapshot: () => GitHubPreviewBrowserContextSnapshot | null;
  getWebsitePreviewContextReference: () => GitHubPreviewContextReference | null;
  setWebsitePreviewBrowserContext: (
    previewId: string,
    context: GitHubPreviewBrowserContextSnapshot | null
  ) => void;
  scopeWebsitePreviewToSession: (
    sessionId: string | null,
    expectedCurrentSessionId?: string
  ) => void;
  clearWebsitePreviewSelection: (expectedContextVersion: number) => Promise<void>;
  registerWebsitePreviewSelectionClear: (handler: (() => void) | null) => void;
  getCanvasContextSnapshot: () => CanvasContextSnapshot | null;
  getCanvasContextReference: () => CanvasContextReference | null;
  setCanvasSelection: (selection: CanvasElementSelection | null, version?: number | null) => void;
  clearCanvasSelection: (expectedContextVersion: number) => void;
  registerCanvasSelectionClear: (handler: (() => void) | null) => void;
}

interface WebsitePreviewAwarenessContextValue {
  context: GitHubPreviewBrowserContextSnapshot | null;
}

interface CanvasAwarenessContextValue {
  context: CanvasContextSnapshot | null;
}

const LivingDocsPanelContext = createContext<LivingDocsPanelContextValue | null>(null);
const LivingDocsPanelActionsContext = createContext<LivingDocsPanelActionsContextValue | null>(
  null
);
const WebsitePreviewAwarenessContext = createContext<WebsitePreviewAwarenessContextValue>({
  context: null,
});
const CanvasAwarenessContext = createContext<CanvasAwarenessContextValue>({ context: null });

export function LivingDocsPanelProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { currentProject } = useProject();
  const [activeReportId, setActiveReportId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const panelCloseHandlerRef = useRef<(() => void) | null>(null);
  const [panelHasCloseControl, setPanelHasCloseControl] = useState(false);
  const [view, setView] = useState<LivingDocsPanelView>('document');
  const [closedAnnotationView, setClosedAnnotationView] = useState<
    'canvas' | 'website-preview' | null
  >(null);
  const [annotationSessionId, setAnnotationSessionId] = useState<string | null>(null);
  const [activeWebsitePreview, setActiveWebsitePreview] = useState<WebsitePreviewMetadata | null>(
    null
  );
  const [activeCanvas, setActiveCanvas] = useState<CanvasToolMetadata | null>(null);
  const [canvasContext, setCanvasContext] = useState<CanvasContextSnapshot | null>(null);
  const [websitePreviewBrowserContext, setWebsitePreviewBrowserContextState] =
    useState<GitHubPreviewBrowserContextSnapshot | null>(null);
  const projectIdRef = useRef(currentProject?.id || null);
  const activeReportIdRef = useRef(activeReportId);
  activeReportIdRef.current = activeReportId;
  const openRef = useRef(open);
  openRef.current = open;
  const viewRef = useRef(view);
  viewRef.current = view;
  const websitePreviewBrowserContextRef = useRef(websitePreviewBrowserContext);
  websitePreviewBrowserContextRef.current = websitePreviewBrowserContext;
  const activeWebsitePreviewRef = useRef(activeWebsitePreview);
  activeWebsitePreviewRef.current = activeWebsitePreview;
  const activeCanvasRef = useRef(activeCanvas);
  activeCanvasRef.current = activeCanvas;
  const canvasContextRef = useRef(canvasContext);
  canvasContextRef.current = canvasContext;
  const canvasSelectionClearRef = useRef<(() => void) | null>(null);
  const websitePreviewSelectionClearRef = useRef<(() => void) | null>(null);
  const websitePreviewSessionIdRef = useRef<string | null>(null);
  const websitePreviewOpenVersionRef = useRef(new Map<string, number>());
  const hibernationRequestedPreviewIdsRef = useRef(new Set<string>());

  const markWebsitePreviewOpen = useCallback((previewId: string) => {
    websitePreviewOpenVersionRef.current.set(
      previewId,
      (websitePreviewOpenVersionRef.current.get(previewId) || 0) + 1
    );
    hibernationRequestedPreviewIdsRef.current.delete(previewId);
  }, []);

  const hibernateWebsitePreview = useCallback(
    (preview: WebsitePreviewMetadata | null) => {
      if (!user || !preview || !['ready', 'validating', 'invalid'].includes(preview.status)) return;
      if (!preview.browserClientId || !preview.browserLeaseToken) return;
      if (hibernationRequestedPreviewIdsRef.current.has(preview.id)) return;
      const openVersion = websitePreviewOpenVersionRef.current.get(preview.id) || 0;
      const browserClientId = preview.browserClientId;
      const browserLeaseToken = preview.browserLeaseToken;
      hibernationRequestedPreviewIdsRef.current.add(preview.id);
      void user
        .getIdToken()
        .then(token => {
          if ((websitePreviewOpenVersionRef.current.get(preview.id) || 0) !== openVersion) {
            hibernationRequestedPreviewIdsRef.current.delete(preview.id);
            return;
          }
          return fetch(`/api/github-previews/${encodeURIComponent(preview.id)}/session`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'hibernate', browserClientId, browserLeaseToken }),
            keepalive: true,
          }).then(response => {
            if (!response.ok) hibernationRequestedPreviewIdsRef.current.delete(preview.id);
          });
        })
        .catch(() => hibernationRequestedPreviewIdsRef.current.delete(preview.id));
    },
    [user]
  );

  const getActiveReportId = useCallback(
    () => (openRef.current && viewRef.current === 'document' ? activeReportIdRef.current : null),
    []
  );
  const getSelectedReportId = useCallback(() => activeReportIdRef.current, []);
  const getWebsitePreviewContextSnapshot = useCallback(
    () => websitePreviewBrowserContextRef.current,
    []
  );
  const getWebsitePreviewContextReference = useCallback(() => {
    const context = websitePreviewBrowserContextRef.current;
    return context
      ? { previewId: context.previewId, contextVersion: context.contextVersion }
      : null;
  }, []);
  const getCanvasContextSnapshot = useCallback(() => canvasContextRef.current, []);
  const getCanvasContextReference = useCallback(() => {
    const context = canvasContextRef.current;
    return context
      ? {
          artifactId: context.artifactId,
          version: context.version,
          contextVersion: context.contextVersion,
        }
      : null;
  }, []);

  const syncCanvasContext = useCallback((canvas: CanvasToolMetadata) => {
    const version = canvas.lastSuccessfulVersion || canvas.version;
    setCanvasContext(current => {
      const next =
        Number.isSafeInteger(version) && version > 0
          ? current?.artifactId === canvas.id && current.version === version
            ? current
            : {
                artifactId: canvas.id,
                version,
                contextVersion: Math.max(Date.now(), (current?.contextVersion || 0) + 1),
                selection: null,
              }
          : null;
      canvasContextRef.current = next;
      return next;
    });
  }, []);

  const selectReport = useCallback((reportId: string | null) => {
    setActiveReportId(reportId);
    setView('document');
    if (reportId) {
      setOpen(true);
    }
  }, []);

  const openPanel = useCallback(
    (reportId?: string | null) => {
      if (viewRef.current === 'tools') setView('document');
      if (reportId) {
        selectReport(reportId);
      }
      if (viewRef.current === 'website-preview' && activeWebsitePreviewRef.current) {
        markWebsitePreviewOpen(activeWebsitePreviewRef.current.id);
      }
      setOpen(true);
    },
    [markWebsitePreviewOpen, selectReport]
  );

  const closePanel = useCallback(() => {
    if (openRef.current && viewRef.current !== 'tools') {
      const closingView = viewRef.current;
      setClosedAnnotationView(
        closingView === 'canvas' || closingView === 'website-preview' ? closingView : null
      );
    }
    if (viewRef.current === 'website-preview') {
      hibernateWebsitePreview(activeWebsitePreviewRef.current);
    }
    setOpen(false);
  }, [hibernateWebsitePreview]);

  const openToolChooser = useCallback(() => {
    setView('tools');
    setOpen(true);
  }, []);

  const registerPanelCloseHandler = useCallback((handler: () => void) => {
    panelCloseHandlerRef.current = handler;
    setPanelHasCloseControl(true);
    return () => {
      if (panelCloseHandlerRef.current === handler) {
        panelCloseHandlerRef.current = null;
        setPanelHasCloseControl(false);
      }
    };
  }, []);

  const requestClosePanel = useCallback(() => {
    if (panelCloseHandlerRef.current) panelCloseHandlerRef.current();
    else closePanel();
  }, [closePanel]);

  const openComments = useCallback((reportId: string) => {
    setActiveReportId(reportId);
    setView('comments');
    setOpen(true);
  }, []);

  const openWebsitePreview = useCallback(
    (preview?: WebsitePreviewMetadata | null) => {
      requestAppRailCollapse();
      if (preview) {
        markWebsitePreviewOpen(preview.id);
        setActiveWebsitePreview(current => {
          if (current?.id !== preview.id) setWebsitePreviewBrowserContextState(null);
          if (!current || current.id !== preview.id) return preview;
          return {
            ...current,
            ...preview,
            browserClientId:
              preview.browserClientId === undefined
                ? current.browserClientId
                : preview.browserClientId,
            browserLeaseToken:
              preview.browserLeaseToken === undefined
                ? current.browserLeaseToken
                : preview.browserLeaseToken,
          };
        });
      } else if (activeWebsitePreviewRef.current) {
        markWebsitePreviewOpen(activeWebsitePreviewRef.current.id);
      }
      setView('website-preview');
      setOpen(true);
    },
    [markWebsitePreviewOpen]
  );

  const openCanvas = useCallback(
    (canvas?: CanvasToolMetadata | null) => {
      if (typeof window !== 'undefined' && !window.matchMedia('(min-width: 62rem)').matches) return;
      requestAppRailCollapse();
      if (viewRef.current === 'website-preview') {
        hibernateWebsitePreview(activeWebsitePreviewRef.current);
      }
      if (canvas) {
        if (
          openRef.current ||
          activeCanvasRef.current?.id !== canvas.id ||
          canvasContextRef.current?.artifactId !== canvas.id
        )
          syncCanvasContext(canvas);
        setActiveCanvas(canvas);
      }
      setView('canvas');
      setOpen(true);
    },
    [hibernateWebsitePreview, syncCanvasContext]
  );

  const openDesignSystem = useCallback(() => {
    if (typeof window !== 'undefined' && !window.matchMedia('(min-width: 62rem)').matches) return;
    requestAppRailCollapse();
    if (viewRef.current === 'website-preview') {
      hibernateWebsitePreview(activeWebsitePreviewRef.current);
    }
    setView('design-system');
    setOpen(true);
  }, [hibernateWebsitePreview]);

  const updateCanvas = useCallback(
    (canvas: CanvasToolMetadata) => {
      const previousLatestVersion =
        activeCanvasRef.current?.lastSuccessfulVersion || activeCanvasRef.current?.version;
      setActiveCanvas(current => (current?.id === canvas.id ? { ...current, ...canvas } : current));
      if (
        canvasContextRef.current?.artifactId === canvas.id &&
        canvasContextRef.current.version >= (previousLatestVersion || 0) &&
        (canvas.status !== 'building' || canvasContextRef.current.version !== canvas.version)
      ) {
        syncCanvasContext(canvas);
      }
      if (
        canvas.status === 'build_failed' &&
        !canvas.lastSuccessfulVersion &&
        canvasContextRef.current?.artifactId === canvas.id
      ) {
        canvasContextRef.current = null;
        setCanvasContext(null);
        canvasSelectionClearRef.current?.();
      }
    },
    [syncCanvasContext]
  );

  const clearCanvas = useCallback((expectedCanvasId?: string) => {
    const activeId = activeCanvasRef.current?.id;
    const contextId = canvasContextRef.current?.artifactId;
    if (expectedCanvasId && activeId !== expectedCanvasId && contextId !== expectedCanvasId) return;
    activeCanvasRef.current = null;
    canvasContextRef.current = null;
    setActiveCanvas(null);
    setCanvasContext(null);
    canvasSelectionClearRef.current?.();
  }, []);

  const toggleCanvas = useCallback(
    (canvas: CanvasToolMetadata) => {
      if (typeof window !== 'undefined' && !window.matchMedia('(min-width: 62rem)').matches) return;
      if (
        openRef.current &&
        viewRef.current === 'canvas' &&
        activeCanvasRef.current?.id === canvas.id
      ) {
        closePanel();
        return;
      }
      requestAppRailCollapse();
      if (viewRef.current === 'website-preview') {
        hibernateWebsitePreview(activeWebsitePreviewRef.current);
      }
      if (
        openRef.current ||
        activeCanvasRef.current?.id !== canvas.id ||
        canvasContextRef.current?.artifactId !== canvas.id
      )
        syncCanvasContext(canvas);
      setActiveCanvas(canvas);
      setView('canvas');
      setOpen(true);
    },
    [closePanel, hibernateWebsitePreview, syncCanvasContext]
  );

  const setCanvasSelection = useCallback(
    (selection: CanvasElementSelection | null, displayedVersion?: number | null) => {
      const canvas = activeCanvasRef.current;
      if (!canvas) return;
      const version = displayedVersion || canvas.lastSuccessfulVersion || canvas.version;
      if (!Number.isSafeInteger(version) || version < 1) return;
      setCanvasContext(current => {
        const next = {
          artifactId: canvas.id,
          version,
          contextVersion: Math.max(Date.now(), (current?.contextVersion || 0) + 1),
          selection,
        };
        canvasContextRef.current = next;
        return next;
      });
    },
    []
  );

  const clearCanvasSelection = useCallback((expectedContextVersion: number) => {
    const current = canvasContextRef.current;
    if (!current?.selection || current.contextVersion !== expectedContextVersion) return;
    const next = {
      ...current,
      contextVersion: Math.max(Date.now(), current.contextVersion + 1),
      selection: null,
    };
    canvasContextRef.current = next;
    setCanvasContext(next);
    canvasSelectionClearRef.current?.();
  }, []);

  const registerCanvasSelectionClear = useCallback((handler: (() => void) | null) => {
    canvasSelectionClearRef.current = handler;
  }, []);

  const updateWebsitePreview = useCallback((preview: WebsitePreviewMetadata) => {
    setActiveWebsitePreview(current => {
      if (!current || current.id !== preview.id) return current;
      const next = {
        ...current,
        ...preview,
        initialPath: preview.initialPath || current.initialPath,
        browserClientId:
          preview.browserClientId === undefined ? current.browserClientId : preview.browserClientId,
        browserLeaseToken:
          preview.browserLeaseToken === undefined
            ? current.browserLeaseToken
            : preview.browserLeaseToken,
      };
      return current.status === next.status &&
        current.revision === next.revision &&
        current.validatedRevision === next.validatedRevision &&
        current.validationStatus === next.validationStatus &&
        current.previewPort === next.previewPort &&
        current.path === next.path &&
        current.initialPath === next.initialPath &&
        current.repository === next.repository &&
        current.browserClientId === next.browserClientId &&
        current.browserLeaseToken === next.browserLeaseToken &&
        current.error === next.error
        ? current
        : next;
    });
  }, []);

  const toggleWebsitePreview = useCallback(
    (preview: WebsitePreviewMetadata) => {
      if (
        openRef.current &&
        viewRef.current === 'website-preview' &&
        activeWebsitePreviewRef.current?.id === preview.id
      ) {
        closePanel();
        return;
      }
      requestAppRailCollapse();
      markWebsitePreviewOpen(preview.id);
      setActiveWebsitePreview(current => {
        if (current?.id !== preview.id) setWebsitePreviewBrowserContextState(null);
        return preview;
      });
      setView('website-preview');
      setOpen(true);
    },
    [closePanel, markWebsitePreviewOpen]
  );

  const registerWebsitePreviewSelectionClear = useCallback((handler: (() => void) | null) => {
    websitePreviewSelectionClearRef.current = handler;
  }, []);

  const setWebsitePreviewBrowserContext = useCallback(
    (previewId: string, context: GitHubPreviewBrowserContextSnapshot | null) => {
      if (activeWebsitePreviewRef.current?.id !== previewId) return;
      if (context && context.previewId !== previewId) return;
      websitePreviewBrowserContextRef.current = context;
      setWebsitePreviewBrowserContextState(context);
    },
    []
  );

  const scopeWebsitePreviewToSession = useCallback(
    (sessionId: string | null, expectedCurrentSessionId?: string) => {
      if (
        expectedCurrentSessionId !== undefined &&
        websitePreviewSessionIdRef.current !== expectedCurrentSessionId
      ) {
        return;
      }
      if (websitePreviewSessionIdRef.current === sessionId) return;
      hibernateWebsitePreview(activeWebsitePreviewRef.current);
      websitePreviewSessionIdRef.current = sessionId;
      setAnnotationSessionId(sessionId);
      websitePreviewBrowserContextRef.current = null;
      activeWebsitePreviewRef.current = null;
      setWebsitePreviewBrowserContextState(null);
      setActiveWebsitePreview(null);
    },
    [hibernateWebsitePreview]
  );

  const clearWebsitePreviewSelection = useCallback(
    async (expectedContextVersion: number) => {
      const current = websitePreviewBrowserContextRef.current;
      if (!current?.selection || current.contextVersion !== expectedContextVersion || !user) {
        return;
      }
      const contextVersion = Math.max(Date.now(), current.contextVersion + 1);
      const optimistic = { ...current, contextVersion, selection: null };
      websitePreviewBrowserContextRef.current = optimistic;
      setWebsitePreviewBrowserContextState(optimistic);
      websitePreviewSelectionClearRef.current?.();
      try {
        const token = await user.getIdToken();
        const response = await fetch(
          `/api/github-previews/${encodeURIComponent(current.previewId)}/browser-context`,
          {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ contextVersion }),
          }
        );
        const payload = (await response.json().catch(() => ({}))) as {
          context?: GitHubPreviewBrowserContextSnapshot | null;
        };
        if (!response.ok || !payload.context) return;
        if (
          websitePreviewBrowserContextRef.current?.previewId === current.previewId &&
          websitePreviewBrowserContextRef.current.contextVersion <= payload.context.contextVersion
        ) {
          websitePreviewBrowserContextRef.current = payload.context;
          setWebsitePreviewBrowserContextState(payload.context);
        }
      } catch {
        // The next browser-context update repairs a transient clear persistence failure.
      }
    },
    [user]
  );

  useEffect(() => {
    const projectId = currentProject?.id || null;
    if (projectIdRef.current === projectId) return;
    const previousProjectId = projectIdRef.current;
    projectIdRef.current = projectId;
    if (!previousProjectId && projectId) return;
    hibernateWebsitePreview(activeWebsitePreviewRef.current);
    activeWebsitePreviewRef.current = null;
    websitePreviewBrowserContextRef.current = null;
    // Keep the chat-session owner intact. A session deep link may switch projects
    // after ChatPanel has already scoped the preview, without remounting ChatPanel.
    // The panel state is project-specific, but its owner changes only when the
    // chat session changes or unmounts.
    setActiveReportId(null);
    setActiveWebsitePreview(null);
    setWebsitePreviewBrowserContextState(null);
    activeCanvasRef.current = null;
    canvasContextRef.current = null;
    setActiveCanvas(null);
    setCanvasContext(null);
    setOpen(false);
  }, [currentProject?.id, hibernateWebsitePreview]);

  const value = useMemo(
    () => ({
      activeReportId,
      activeWebsitePreview,
      websitePreviewSessionId: annotationSessionId,
      activeCanvas,
      open,
      view,
      openToolChooser,
      requestClosePanel,
      panelHasCloseControl,
      openPanel,
      openComments,
      openWebsitePreview,
      updateWebsitePreview,
      toggleWebsitePreview,
      openCanvas,
      openDesignSystem,
      updateCanvas,
      toggleCanvas,
      clearCanvas,
      setCanvasSelection,
      registerCanvasSelectionClear,
      closePanel,
      selectReport,
      setView,
    }),
    [
      activeReportId,
      activeWebsitePreview,
      annotationSessionId,
      activeCanvas,
      closePanel,
      open,
      openToolChooser,
      requestClosePanel,
      panelHasCloseControl,
      openComments,
      openPanel,
      openWebsitePreview,
      openCanvas,
      openDesignSystem,
      updateCanvas,
      toggleCanvas,
      clearCanvas,
      setCanvasSelection,
      registerCanvasSelectionClear,
      updateWebsitePreview,
      toggleWebsitePreview,
      selectReport,
      view,
    ]
  );
  const actions = useMemo(
    () => ({
      registerPanelCloseHandler,
      openPanel,
      openComments,
      openWebsitePreview,
      updateWebsitePreview,
      toggleWebsitePreview,
      openCanvas,
      openDesignSystem,
      updateCanvas,
      toggleCanvas,
      clearCanvas,
      closePanel,
      selectReport,
      getActiveReportId,
      getSelectedReportId,
      getWebsitePreviewContextSnapshot,
      getWebsitePreviewContextReference,
      setWebsitePreviewBrowserContext,
      scopeWebsitePreviewToSession,
      clearWebsitePreviewSelection,
      registerWebsitePreviewSelectionClear,
      getCanvasContextSnapshot,
      getCanvasContextReference,
      setCanvasSelection,
      clearCanvasSelection,
      registerCanvasSelectionClear,
    }),
    [
      clearWebsitePreviewSelection,
      registerPanelCloseHandler,
      closePanel,
      getActiveReportId,
      getSelectedReportId,
      getWebsitePreviewContextSnapshot,
      getWebsitePreviewContextReference,
      getCanvasContextSnapshot,
      getCanvasContextReference,
      openComments,
      openPanel,
      openWebsitePreview,
      openCanvas,
      openDesignSystem,
      updateCanvas,
      toggleCanvas,
      clearCanvas,
      updateWebsitePreview,
      toggleWebsitePreview,
      registerWebsitePreviewSelectionClear,
      setCanvasSelection,
      clearCanvasSelection,
      registerCanvasSelectionClear,
      selectReport,
      scopeWebsitePreviewToSession,
    ]
  );

  // AppLayout returns to the document view when an editor closes; its attachment stays in chat.
  const annotationView =
    view === 'tools' || (!open && view === 'document') ? closedAnnotationView : view;
  const annotationTarget: PreviewAnnotationTarget | null =
    annotationView === 'canvas' && activeCanvas && canvasContext?.artifactId === activeCanvas.id
      ? { kind: 'canvas', artifactId: canvasContext.artifactId, version: canvasContext.version }
      : annotationView === 'website-preview' && activeWebsitePreview
        ? {
            kind: 'website',
            previewId: activeWebsitePreview.id,
            revision: activeWebsitePreview.revision,
            path:
              websitePreviewBrowserContext?.previewId === activeWebsitePreview.id
                ? websitePreviewBrowserContext.path
                : activeWebsitePreview.initialPath || '/',
          }
        : null;

  return (
    <PreviewAnnotationProvider
      scopeKey={`${currentProject?.id || ''}:${annotationSessionId || ''}`}
      target={annotationTarget}
      targetResetKey={
        annotationView === 'website-preview' ? activeWebsitePreview?.initialPath || '' : ''
      }
    >
      <LivingDocsPanelActionsContext.Provider value={actions}>
        <CanvasAwarenessContext.Provider value={{ context: canvasContext }}>
          <WebsitePreviewAwarenessContext.Provider
            value={{ context: websitePreviewBrowserContext }}
          >
            <LivingDocsPanelContext.Provider value={value}>
              {children}
            </LivingDocsPanelContext.Provider>
          </WebsitePreviewAwarenessContext.Provider>
        </CanvasAwarenessContext.Provider>
      </LivingDocsPanelActionsContext.Provider>
    </PreviewAnnotationProvider>
  );
}

export function useLivingDocsPanel() {
  const context = useContext(LivingDocsPanelContext);
  if (!context) throw new Error('useLivingDocsPanel must be used within LivingDocsPanelProvider');
  return context;
}

export function useLivingDocsPanelActions() {
  const context = useContext(LivingDocsPanelActionsContext);
  if (!context) {
    throw new Error('useLivingDocsPanelActions must be used within LivingDocsPanelProvider');
  }
  return context;
}

/** Keep the shell toggle routed through the active tool's close/save behavior. */
export function useLivingDocsPanelClose(onClose: () => void, enabled: boolean) {
  const { registerPanelCloseHandler } = useLivingDocsPanelActions();
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (enabled) return registerPanelCloseHandler(() => closeRef.current());
  }, [enabled, registerPanelCloseHandler]);
}

export function useWebsitePreviewSessionScope(
  sessionId: string | null,
  requestedSessionId: string | null = sessionId
) {
  const { websitePreviewSessionId } = useLivingDocsPanel();
  const { scopeWebsitePreviewToSession } = useLivingDocsPanelActions();
  const scopeEffectVersionRef = useRef(0);
  // Only a launch that already claimed the destination may transfer ownership
  // before hydration. Ordinary deep links keep the loaded chat until they succeed.
  const scopedSessionId =
    requestedSessionId && requestedSessionId === websitePreviewSessionId
      ? requestedSessionId
      : sessionId;
  useEffect(() => {
    const effectVersion = ++scopeEffectVersionRef.current;
    scopeWebsitePreviewToSession(scopedSessionId);
    return () => {
      if (!scopedSessionId) return;
      // Strict Mode immediately re-establishes this effect. Let that setup cancel
      // cleanup; a real unmount still releases the preview at the next microtask.
      queueMicrotask(() => {
        if (scopeEffectVersionRef.current === effectVersion) {
          scopeWebsitePreviewToSession(null, scopedSessionId);
        }
      });
    };
  }, [scopeWebsitePreviewToSession, scopedSessionId]);
}

export function useWebsitePreviewAwareness() {
  return useContext(WebsitePreviewAwarenessContext);
}

export function useCanvasAwareness() {
  return useContext(CanvasAwarenessContext);
}
